import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, apiClient } from '../../lib/api';

interface TvVersionInfo {
  version: string | null;
  downloadUrl?: string;
  releaseDate?: string;
  fileName?: string;
  fileSize?: number;
  message?: string;
}

export default function TvAppSetupPage() {
  const { t, i18n } = useTranslation('admin');
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [version, setVersion] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const apiBase =
    (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';

  const { data: tvInfo, isLoading } = useQuery<TvVersionInfo>({
    queryKey: ['tv-version'],
    queryFn: () => api.get<TvVersionInfo>('/tv/version'),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/tv/upload?version=${encodeURIComponent(version)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tv-version'] });
      setVersion('');
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  const venueUrl = `${window.location.origin}/tv`;
  const hasApk = tvInfo?.version != null;

  const formatSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-base-content">{t('tvAppTitle')}</h1>
        <p className="text-base-content/60 mt-1">{t('tvAppSubtitle')}</p>
      </div>

      {/* Current Version Card */}
      <div className="bg-base-200 rounded-xl p-6 border border-base-300">
        <h2 className="text-lg font-semibold text-base-content mb-4">{t('tvAppCurrentVersion')}</h2>
        {isLoading ? (
          <div className="h-20 bg-base-300 rounded-lg animate-pulse" />
        ) : hasApk ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-base-content/50">{t('tvAppVersion')}</span>
                <p className="font-mono font-bold text-lg text-primary">v{tvInfo.version}</p>
              </div>
              <div>
                <span className="text-base-content/50">{t('tvAppFileSize')}</span>
                <p className="font-semibold">{formatSize(tvInfo.fileSize)}</p>
              </div>
              <div className="col-span-2">
                <span className="text-base-content/50">{t('tvAppReleaseDate')}</span>
                <p className="font-semibold">{formatDate(tvInfo.releaseDate)}</p>
              </div>
            </div>
            <a
              href={`${apiBase}/api/tv/download`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-content rounded-lg font-semibold hover:opacity-90 transition-opacity"
              download
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {t('tvAppDownload')}
            </a>
          </div>
        ) : (
          <p className="text-base-content/50">{t('tvAppNoApk')}</p>
        )}
      </div>

      {/* Upload Card */}
      <div className="bg-base-200 rounded-xl p-6 border border-base-300">
        <h2 className="text-lg font-semibold text-base-content mb-4">{t('tvAppUpload')}</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".apk"
            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            className="file-input file-input-bordered flex-1"
          />
          <input
            type="text"
            placeholder="X.Y.Z"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="input input-bordered w-full sm:w-36"
          />
          <button
            onClick={() => {
              if (selectedFile && version.trim()) uploadMutation.mutate(selectedFile);
            }}
            disabled={uploadMutation.isPending || !version.trim() || !selectedFile}
            className="btn btn-primary"
          >
            {uploadMutation.isPending ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              <span className="material-symbols-outlined text-lg">upload</span>
            )}
            {t('tvAppUpload')}
          </button>
        </div>
        {uploadMutation.isSuccess && (
          <p className="text-success mt-3 text-sm font-semibold">{t('tvAppUploadSuccess')}</p>
        )}
        {uploadMutation.isError && (
          <p className="text-error mt-3 text-sm">
            {(uploadMutation.error as Error)?.message || t('tvAppUpload') + ' failed'}
          </p>
        )}
      </div>

      {/* Venue URL Card */}
      <div className="bg-base-200 rounded-xl p-6 border border-base-300">
        <h2 className="text-lg font-semibold text-base-content mb-2">{t('tvAppVenueUrl')}</h2>
        <p className="text-base-content/60 text-sm mb-3">{t('tvAppVenueUrlDesc')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-base-300 px-4 py-3 rounded-lg font-mono text-lg text-primary select-all">
            {venueUrl}
          </code>
          <button
            onClick={() => navigator.clipboard.writeText(venueUrl)}
            className="btn btn-ghost btn-square"
            aria-label="Copy URL"
          >
            <span className="material-symbols-outlined">content_copy</span>
          </button>
        </div>
      </div>

      {/* Installation Steps */}
      <div className="bg-base-200 rounded-xl p-6 border border-base-300">
        <h2 className="text-lg font-semibold text-base-content mb-4">{t('tvAppInstallTitle')}</h2>
        <ol className="space-y-3 text-base-content/80">
          {[1, 2, 3, 4].map((step) => (
            <li key={step} className="flex items-start gap-3">
              <span className="flex-none w-7 h-7 rounded-full bg-primary text-primary-content flex items-center justify-center text-sm font-bold">
                {step}
              </span>
              <span className="pt-0.5">{t(`tvAppStep${step}`)}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
